import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '@/context/CartContext';
import { fmtAUD } from '@/lib/format';
import { Trash2, Plus, Minus, ArrowRight } from 'lucide-react';
import { Toaster } from 'sonner';

export default function Cart() {
    const { items, subtotal, setQty, remove, clear } = useCart();
    const nav = useNavigate();

    if (items.length === 0) {
        return (
            <div className="text-center py-20" data-testid="cart-empty">
                <Toaster theme="dark" />
                <div className="font-mono-data text-xs uppercase text-zinc-500 mb-3">Your cart</div>
                <h1 className="font-display text-4xl uppercase tracking-tighter mb-4">Nothing in here yet.</h1>
                <p className="text-zinc-400 mb-8">Add gear from the catalog to build your quote.</p>
                <Link to="/" className="bg-brand hover:bg-brand-hover text-black font-display uppercase tracking-wider px-6 py-3 inline-flex items-center gap-2">
                    Browse catalog <ArrowRight size={16} />
                </Link>
            </div>
        );
    }

    return (
        <div data-testid="cart-page">
            <Toaster theme="dark" />
            <div className="flex items-center justify-between mb-8">
                <div>
                    <div className="font-mono-data text-xs uppercase text-crimson tracking-widest mb-2">Your Quote</div>
                    <h1 className="font-display text-4xl uppercase tracking-tighter">Cart</h1>
                </div>
                <button data-testid="cart-clear-btn" onClick={clear}
                        className="font-mono-data text-xs uppercase text-zinc-500 hover:text-crimson">
                    Clear all
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                <div className="lg:col-span-8 border-hairline">
                    <div className="grid grid-cols-12 gap-0 font-mono-data text-[10px] uppercase text-zinc-500 px-5 py-3 border-b border-zinc-800 bg-surface">
                        <div className="col-span-6">Item</div>
                        <div className="col-span-2 text-center">Qty</div>
                        <div className="col-span-2 text-right">Unit</div>
                        <div className="col-span-2 text-right">Line</div>
                    </div>
                    {items.map((i) => (
                        <div key={i.key} data-testid={`cart-row-${i.sku}`}
                             className="grid grid-cols-12 items-center gap-3 px-5 py-4 border-b border-zinc-800/60">
                            <div className="col-span-6 flex items-center gap-4">
                                <img src={i.image_url} alt="" className="w-14 h-14 object-cover border border-zinc-800" />
                                <div>
                                    <div className="font-display uppercase text-sm tracking-tight">{i.product_name}</div>
                                    <div className="font-mono-data text-xs text-zinc-400">{i.variant_label}</div>
                                    <div className="font-mono-data text-[10px] text-zinc-500">{i.sku}</div>
                                </div>
                            </div>
                            <div className="col-span-2 flex items-center justify-center gap-2">
                                <button onClick={() => setQty(i.key, i.qty - 1)}
                                        className="p-1 border border-zinc-800 hover:border-crimson hover:text-crimson">
                                    <Minus size={12} />
                                </button>
                                <input
                                    type="number" min="0" value={i.qty}
                                    onChange={(e) => setQty(i.key, parseInt(e.target.value || '0', 10))}
                                    className="w-14 bg-surface border border-zinc-800 px-2 py-1 text-center font-mono-data text-sm" />
                                <button onClick={() => setQty(i.key, i.qty + 1)}
                                        className="p-1 border border-zinc-800 hover:border-crimson hover:text-crimson">
                                    <Plus size={12} />
                                </button>
                            </div>
                            <div className="col-span-2 text-right font-mono-data text-sm">{fmtAUD(i.unit_price)}</div>
                            <div className="col-span-2 text-right flex items-center justify-end gap-3">
                                <span className="font-mono-data text-sm">{fmtAUD(i.unit_price * i.qty)}</span>
                                <button data-testid={`cart-remove-${i.sku}`} onClick={() => remove(i.key)}
                                        className="text-zinc-500 hover:text-crimson">
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>

                <aside className="lg:col-span-4 border-hairline p-6 self-start sticky top-24">
                    <div className="font-mono-data text-[10px] uppercase text-zinc-500 mb-1">Subtotal</div>
                    <div data-testid="cart-subtotal" className="font-display text-4xl mb-2">{fmtAUD(subtotal)}</div>
                    <div className="font-mono-data text-[10px] uppercase text-zinc-500 mb-6">
                        AUD · ex GST · {items.reduce((s, i) => s + i.qty, 0)} units
                    </div>
                    <button
                        data-testid="cart-checkout-btn"
                        onClick={() => nav('/checkout')}
                        className="w-full bg-brand hover:bg-brand-hover text-black font-display uppercase tracking-wider py-3 transition-colors">
                        Proceed to Checkout
                    </button>
                    <Link to="/" className="block text-center mt-3 font-mono-data text-xs uppercase text-zinc-500 hover:text-crimson">
                        Continue shopping
                    </Link>
                </aside>
            </div>
        </div>
    );
}
